// ─── ErrorBoundary.jsx — Catches React render errors, shows recovery UI ─────
import { Component } from "react";
import { captureError } from "../lib/analytics.js";

const FONT = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// ─── Full-page Error Boundary (wraps entire app) ────────────────────────────
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("CashHorizon Error Boundary caught:", error, errorInfo);
    // Report render crashes to Sentry so they're visible in observability,
    // not just the console. Tagged so we can filter root-boundary crashes
    // from section-boundary ones in the Sentry UI.
    captureError(error, {
      boundary: "root",
      componentStack: errorInfo?.componentStack?.slice(0, 2000),
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh", background: "#0d0f1a",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontFamily: FONT, padding: 24,
        }}>
          <div style={{
            background: "#131629", border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 24, padding: "48px 40px", maxWidth: 460, width: "100%",
            textAlign: "center", boxShadow: "0 12px 48px rgba(0,0,0,0.7)",
          }}>
            {/* Logo — animated gradient square matching LoadingScreen */}
            <div style={{ position:"relative", width:72, height:72, margin:"0 auto 20px", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ width:64, height:64, borderRadius:18, background:"linear-gradient(135deg,#8b5cf6,#06b6d4)", boxShadow:"0 12px 40px rgba(139,92,246,0.5)" }}/>
              <div style={{ position:"absolute", inset:-4, borderRadius:22, border:"3px solid transparent", borderTopColor:"#8b5cf6", borderRightColor:"#06b6d4", animation:"chSpin 1.2s linear infinite" }}/>
              <div style={{ position:"absolute", inset:-9, borderRadius:27, border:"1.5px solid transparent", borderBottomColor:"#8b5cf6", borderLeftColor:"#06b6d4", opacity:0.35, animation:"chSpin 2.4s linear infinite reverse" }}/>
            </div>
            <style>{`@keyframes chSpin{to{transform:rotate(360deg)}}`}</style>

            <div style={{ fontSize: 22, fontWeight: 800, color: "#f0f2ff", marginBottom: 8, letterSpacing: -0.5 }}>
              Something went wrong
            </div>
            <div style={{ fontSize: 14, color: "#b0b4cc", lineHeight: 1.6, marginBottom: 28 }}>
              An unexpected error occurred. Your data is safe — this is a display issue. Try reloading the page.
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
              <button onClick={() => window.location.reload()} style={{
                background: "linear-gradient(135deg, #8b5cf6, #06b6d4)", color: "#fff",
                border: "none", borderRadius: 12, padding: "13px 28px",
                fontSize: 15, fontWeight: 700, fontFamily: FONT, cursor: "pointer",
                boxShadow: "0 8px 24px rgba(139,92,246,0.35)",
              }}>Reload Page</button>

              <button onClick={() => { window.location.hash = "dashboard"; window.location.reload(); }} style={{
                background: "transparent", color: "#b0b4cc",
                border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12,
                padding: "13px 24px", fontSize: 14, fontFamily: FONT, cursor: "pointer",
              }}>Go to Dashboard</button>
            </div>

            {/* Error details (collapsed) */}
            {this.state.error && (
              <details style={{ marginTop: 24, textAlign: "left" }}>
                <summary style={{ fontSize: 11, color: "#636780", cursor: "pointer", marginBottom: 8 }}>
                  Technical details
                </summary>
                <div style={{
                  background: "#0d0f1a", borderRadius: 8, padding: 12,
                  fontSize: 11, color: "#ef4444", fontFamily: "monospace",
                  whiteSpace: "pre-wrap", wordBreak: "break-all",
                  maxHeight: 120, overflow: "auto", lineHeight: 1.5,
                  border: "1px solid rgba(239,68,68,0.2)",
                }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack?.slice(0, 500)}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ─── Section-level Error Boundary (wraps individual pages/widgets) ───────────
// Shows an inline recovery card instead of crashing the entire app.
// Props:
//   name     — display name for the section ("Dashboard", "Chat", etc.)
//   variant  — "page" (default) or "widget" (compact fallback)
//   resetKey — changing this value auto-recovers (use page name or route)
//
// Usage:
//   <SectionBoundary name="Dashboard" resetKey={page}>
//     <Dashboard .../>
//   </SectionBoundary>

export class SectionBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this._retryCount = 0;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    const name = this.props.name || "Section";
    console.error(`[SectionBoundary:${name}] caught:`, error?.message || error);
    captureError(error, {
      boundary: "section",
      section: name,
      componentStack: errorInfo?.componentStack?.slice(0, 2000),
    });
  }

  componentDidUpdate(prevProps) {
    // Auto-recover when the page/key changes (user navigated away and back)
    if (this.state.hasError && this.props.resetKey !== prevProps.resetKey) {
      this.setState({ hasError: false, error: null });
      this._retryCount = 0;
    }
  }

  handleRetry = () => {
    this._retryCount++;
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const name = this.props.name || "This section";
      const isWidget = this.props.variant === "widget";
      const canRetry = this._retryCount < 3;

      // Minimal inline fallback for small widgets
      if (isWidget) {
        return (
          <div style={{
            padding: "12px 16px", borderRadius: 12,
            background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)",
            fontSize: 12, color: "#b0b4cc", fontFamily: FONT, textAlign: "center",
          }}>
            <span style={{ fontSize: 16 }}>⚠️</span>
            {canRetry && (
              <button onClick={this.handleRetry} style={{
                background: "none", border: "none", color: "#ef4444",
                fontSize: 12, fontFamily: FONT, cursor: "pointer", textDecoration: "underline", marginLeft: 8,
              }}>Retry</button>
            )}
          </div>
        );
      }

      // Full inline fallback for pages
      return (
        <div style={{
          padding: "40px 24px", textAlign: "center", fontFamily: FONT,
          borderRadius: 16, margin: "12px 0",
          background: "rgba(239,68,68,0.04)", border: "1px solid rgba(239,68,68,0.12)",
        }}>
          <div style={{ fontSize: 36, marginBottom: 12, opacity: 0.5 }}>⚠️</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#f0f2ff", marginBottom: 8 }}>
            {name} encountered an issue
          </div>
          <div style={{ fontSize: 13, color: "#b0b4cc", lineHeight: 1.6, marginBottom: 20, maxWidth: 400, margin: "0 auto 20px" }}>
            This section had a problem loading. The rest of the app is unaffected and your data is safe.
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
            {canRetry && (
              <button onClick={this.handleRetry} style={{
                background: "#ef4444", color: "#fff", border: "none", borderRadius: 10,
                padding: "10px 22px", fontSize: 13, fontWeight: 700, fontFamily: FONT,
                cursor: "pointer", boxShadow: "0 4px 16px rgba(239,68,68,0.3)",
              }}>Try Again</button>
            )}
            <button onClick={() => window.location.reload()} style={{
              background: "transparent", color: "#b0b4cc",
              border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10,
              padding: "10px 22px", fontSize: 13, fontFamily: FONT, cursor: "pointer",
            }}>Reload Page</button>
          </div>
          {this.state.error && (
            <details style={{ marginTop: 16, textAlign: "left", maxWidth: 400, margin: "16px auto 0" }}>
              <summary style={{ fontSize: 10, color: "#636780", cursor: "pointer" }}>Details</summary>
              <div style={{
                background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: 8, marginTop: 6,
                fontSize: 10, color: "#ef4444", fontFamily: "monospace",
                whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 80, overflow: "auto",
              }}>
                {this.state.error?.message || this.state.error?.toString?.() || "Unknown error"}
              </div>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
